import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import { api, clearAuth, getStoredAuth, updateStoredUser } from "./api/client";

import LoginScreen from "./views/LoginScreen";
import MainLayout from "./components/MainLayout";
import ChatView from "./views/ChatView";
import KnowledgeView from "./views/KnowledgeView";
import VoiceView from "./views/VoiceView";
import AuditView from "./views/AuditView";
import QwenVlDebugView from "./views/QwenVlDebugView";
import UsersView from "./views/UsersView";
import RolesView from "./views/RolesView";
import ProfileView from "./views/ProfileView";

function hasPerm(user, perm) {
  return (user?.permissions || []).includes(perm);
}

function hasRole(user, roles) {
  return roles.some((r) => (user?.roles || []).includes(r));
}

function ProtectedRoute({ user, perm, role, children }) {
  const allowed = perm ? hasPerm(user, perm) : role ? hasRole(user, [role]) : true;
  return allowed ? children : <Navigate to="/chat" replace />;
}

function App() {
  const [user, setUser] = useState(() => getStoredAuth().user);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getStoredAuth().accessToken) {
      setChecking(false);
      return;
    }
    api.me()
      .then((profile) => {
        updateStoredUser(profile);
        setUser(profile);
      })
      .catch(() => {
        clearAuth();
        setUser(null);
      })
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <main className="h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#10a37f] flex items-center justify-center">
            <span className="material-symbols-outlined text-white text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>bolt</span>
          </div>
          <p className="text-gray-400 text-sm font-medium">Memuat aplikasi...</p>
        </div>
      </main>
    );
  }

  if (!user) return <LoginScreen onLogin={setUser} />;

  return (
    <Routes>
      <Route path="/" element={<MainLayout user={user} setUser={setUser} />}>
        <Route index element={<Navigate to="/chat" replace />} />
        <Route path="chat" element={<ChatView />} />
        <Route path="knowledge" element={<KnowledgeView />} />
        <Route path="voice" element={<VoiceView />} />
        <Route
          path="audit"
          element={
            <ProtectedRoute user={user} perm="audit.read">
              <AuditView />
            </ProtectedRoute>
          }
        />
        <Route
          path="debug/qwen-vl"
          element={
            <ProtectedRoute user={user} perm="audit.read">
              <QwenVlDebugView />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute user={user} perm="user.manage">
              <UsersView />
            </ProtectedRoute>
          }
        />
        <Route
          path="roles"
          element={
            <ProtectedRoute user={user} perm="role.manage">
              <RolesView />
            </ProtectedRoute>
          }
        />
        <Route path="profile" element={<ProfileView />} />
        <Route path="*" element={<Navigate to="/chat" replace />} />
      </Route>
    </Routes>
  );
}

export default App;
